import type { Map as MapLibreMap } from 'maplibre-gl'

import { CogSourceSettings } from '@types'

import {
  COG_PROTOCOL_PREFIX,
  DEFAULT_RASTER_OPACITY,
  DEFAULT_TILE_SIZE,
  MAP_DATA_BASE_PATH,
} from './constants'

/**
 * Строит URL для COG-протокола на основе настроек источника
 *
 * @param cogSource - Настройки COG-источника
 * @returns Полный URL вида `cog://<HTTP_URL>`
 *
 * @example
 * Локальный файл:
 * ```ts
 * buildCogUrl({ fileName: 'demo.tif', ... })
 * // => 'cog:///map-data/demo.tif'
 * ```
 *
 * @example
 * Удалённый файл:
 * ```ts
 * buildCogUrl({ fileName: 'demo.tif', url: 'https://example.com/data.tif', ... })
 * // => 'cog://https://example.com/data.tif'
 * ```
 */
export function buildCogUrl(cogSource: CogSourceSettings): string {
  const { fileName, url } = cogSource

  // Если задан полный URL, используем его
  if (url) {
    return `${COG_PROTOCOL_PREFIX}${url}`
  }

  // Иначе строим локальный путь
  const safeFileName = fileName.replace(/^\//, '')
  const httpUrl = `${MAP_DATA_BASE_PATH}/${safeFileName}`

  return `${COG_PROTOCOL_PREFIX}${httpUrl}`
}

/**
 * Инициализирует COG-источник и слой на карте
 *
 * @param map - Экземпляр MapLibre карты
 * @param cogSource - Настройки COG-источника
 *
 * @throws {Error} Если не удалось добавить источник или слой
 *
 * @remarks
 * - Не добавляет источник, если он уже существует
 * - Не добавляет слой, если он уже существует
 * - Применяет видимость слоя согласно `enabledByDefault`
 */
export function initializeCogLayer(map: MapLibreMap, cogSource: CogSourceSettings): void {
  const { id, layerId, tileSize, opacity, enabledByDefault } = cogSource

  const cogUrl = buildCogUrl(cogSource)

  // Добавляем источник, если его ещё нет
  if (!map.getSource(id)) {
    map.addSource(id, {
      type: 'raster',
      url: cogUrl,
      tileSize: tileSize ?? DEFAULT_TILE_SIZE,
    })
  }

  // Добавляем слой, если его ещё нет
  if (!map.getLayer(layerId)) {
    map.addLayer({
      id: layerId,
      type: 'raster',
      source: id,
      paint: {
        'raster-opacity': opacity ?? DEFAULT_RASTER_OPACITY,
      },
    })

    // Применяем начальную видимость
    if (enabledByDefault === false) {
      map.setLayoutProperty(layerId, 'visibility', 'none')
    }
  }
}

/**
 * Переключает видимость COG-слоя
 *
 * @param map - Экземпляр MapLibre карты
 * @param layerId - ID слоя для переключения
 * @param visible - Флаг видимости (true - показать, false - скрыть)
 *
 * @throws {Error} Если слой не найден или операция не удалась
 */
export function toggleCogVisibility(map: MapLibreMap, layerId: string, visible: boolean): void {
  const visibility = visible ? 'visible' : 'none'
  map.setLayoutProperty(layerId, 'visibility', visibility)
}
